import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { debounce } from 'lodash';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import { TestContactsTableComponent } from '../../../../components/tables/settings/test-contacts-table/test-contacts-table.component';
import { AddTestUserFormComponent } from '../../../../components/forms/settingsforms/add-test-user-form/add-test-user-form.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { ToastService } from '../../../../services/toast/toast.service';
import { ITestContact, TBlkCxpRole } from '../../../../types/settingsDataModule';
import { TEAM_TAB_ROLES, hasAnyRole } from '../settings-roles';
import { formatCount } from '../../../../../utils/sms';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/**
 * Test numbers: the phones that receive a test send before a real one.
 * This page keeps its own list and count, so a search here never changes
 * the test-contact picker in the composer.
 */
@Component({
  selector: 'app-test-contacts',
  standalone: true,
  imports: [CommonModule, SearchInputComponent, TestContactsTableComponent, AddTestUserFormComponent],
  templateUrl: './test-contacts.component.html',
  styleUrls: ['../settings-ui.css', './test-contacts.component.css'],
})
export class TestContactsComponent implements OnInit, OnDestroy {
  constructor() {
    // Bind the debounced function
    this.debouncedSearch = debounce(this.searchTestContacts.bind(this), 400);
  }

  showAddUser = false;
  settingsService = inject(SettingsService);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  // Whether the current user is allowed to create test contacts
  canCreateTestUser = false;

  testContacts: ITestContact[] = [];
  /** How many test numbers match the current search (all of them without one). */
  testContactsCount: number = 0;
  searchTerm: string = '';

  /** True while a page is on its way. */
  loading = true;
  loadFailed = false;
  page = 0;
  pageSize = 10;

  // params
  params: Record<string, any> = { size: 10 };

  // Debounced method
  debouncedSearch!: (...args: any[]) => void;

  // Feedback goes to the toast rail
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;

  /** Each request replaces the one before, so a slow answer never overwrites a newer one. */
  private load$ = new Subject<void>();

  get reactiveParams() {
    return this.params;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.testContactsCount / this.pageSize));
  }

  get summary(): string {
    const count = formatCount(this.testContactsCount);
    if (this.searchTerm.trim()) {
      return this.testContactsCount === 1 ? '1 number matches' : `${count} numbers match`;
    }
    return this.testContactsCount === 1 ? '1 test number' : `${count} test numbers`;
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    delete this.params['firstName.contains'];
    delete this.params['lastName.contains'];
    delete this.params['emailAddress.contains'];
    delete this.params['primaryContactNumber.contains'];

    const value = term.trim();
    if (value) {
      // Digits search the number; anything else searches first names.
      const digits = value.replace(/[\s()-]/g, '');
      if (/^\+?\d+$/.test(digits)) {
        this.params['primaryContactNumber.contains'] = digits.replace(/^\+/, '');
      } else {
        this.params['firstName.contains'] = value;
      }
    }
    this.debouncedSearch();
  }

  searchTestContacts() {
    this.page = 0;
    this.reload();
  }

  /** Fetches the current page and the matching total. */
  reload() {
    this.load$.next();
  }

  private criteria(): Record<string, any> {
    const { size, page, ...criteria } = this.params;
    return criteria;
  }

  ngOnInit() {
    // Determine whether the current user can create test contacts
    this.settingsService.getUserRoles
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userRoles: TBlkCxpRole[]) => {
        this.canCreateTestUser = hasAnyRole(userRoles, TEAM_TAB_ROLES);
      });

    this.load$
      .pipe(
        tap(() => {
          this.loading = true;
          this.loadFailed = false;
        }),
        switchMap(() => {
          const criteria = this.criteria();
          this.params['size'] = this.pageSize;
          return forkJoin({
            list: this.settingsService.listTestContacts({ ...criteria, page: this.page, size: this.pageSize }),
            count: this.settingsService.countTestContacts(criteria).pipe(catchError(() => of(-1))),
          }).pipe(
            map((result) => ({ ok: true as const, ...result })),
            catchError(() => of({ ok: false as const, list: [] as ITestContact[], count: 0 }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.loading = false;
        if (!result.ok) {
          this.loadFailed = true;
          this.displayFeedback('The test numbers did not load. Check your connection and try again.', 'error');
          return;
        }
        this.testContacts = result.list;
        // If the count failed, the list itself is the best estimate.
        this.testContactsCount =
          result.count >= 0 ? result.count : this.page * this.pageSize + result.list.length;

        // Deleting the last number on a page steps back a page.
        const lastPage = Math.max(0, this.totalPages - 1);
        if (!result.list.length && this.page > lastPage) {
          this.page = lastPage;
          this.reload();
        }
      });

    this.reload();
  }

  onPageChange(page: number) {
    this.page = Math.max(0, Number(page) || 0);
    this.reload();
  }

  onPageSizeChange(size: number) {
    // The pagination emits a page change right after, which reloads.
    this.pageSize = Number(size) || 10;
    this.params['size'] = this.pageSize;
  }

  onAddSuccess() {
    this.displayFeedback('Test number added. Send it a test from its row.', 'success');
  }

  displayFeedback(message: string, type: FeedbackType) {
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    if (type === 'error' || type === 'warning') {
      this.toast.error(message);
    } else {
      this.toast.success(message);
    }
  }

  ngOnDestroy() {
    (this.debouncedSearch as any)?.cancel?.();
  }
}
